import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useConnect, useAccount } from 'wagmi';
import { createConnector } from '@wagmi/core';
import { injected } from 'wagmi/connectors';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'qrcode';
import EthereumProvider from '@walletconnect/ethereum-provider';
import { getAddress } from 'viem';

const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '';

function createWcQrConnector(provider: any) {
  return createConnector((config) => ({
    id: 'walletConnectQr',
    name: 'WalletConnect QR',
    type: 'walletConnect',
    async connect() {
      const accounts = provider.accounts.map((a: string) => getAddress(a));
      const chainId = provider.chainId;
      provider.on('accountsChanged', (accts: string[]) => {
        config.emitter.emit('change', { accounts: accts.map((a: string) => getAddress(a)) });
      });
      provider.on('chainChanged', (id: string) => {
        config.emitter.emit('change', { chainId: Number(id) });
      });
      provider.on('disconnect', () => {
        config.emitter.emit('disconnect');
      });
      return { accounts, chainId };
    },
    async disconnect() {
      try {
        provider.removeAllListeners?.();
        await provider.disconnect();
      } catch {}
    },
    async getAccounts() {
      return provider.accounts.map((a: string) => getAddress(a));
    },
    async getChainId() {
      return provider.chainId;
    },
    async getProvider() {
      return provider;
    },
    async isAuthorized() {
      return provider.accounts.length > 0;
    },
    onAccountsChanged(accounts: string[]) {
      config.emitter.emit('change', { accounts: accounts.map((a: string) => getAddress(a)) });
    },
    onChainChanged(chainId: string) {
      config.emitter.emit('change', { chainId: Number(chainId) });
    },
    onDisconnect() {
      config.emitter.emit('disconnect');
    },
  }));
}

interface WalletConnectModalProps {
  open: boolean;
  onClose: () => void;
}

type ModalView = 'select' | 'qr';

function WalletConnectModalContent({ open, onClose }: WalletConnectModalProps) {
  const { connect, isPending } = useConnect();
  const { isConnected } = useAccount();
  const [selected, setSelected] = useState<'browser' | 'mobile' | null>(null);
  const [view, setView] = useState<ModalView>('select');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [wcUri, setWcUri] = useState<string>('');
  const [wcError, setWcError] = useState<string>('');
  const providerRef = useRef<any>(null);
  const uriHandlerRef = useRef<((uri: string) => void) | null>(null);
  const cancelledRef = useRef(false);

  const hasProjectId = projectId && projectId !== 'placeholder-project-id';

  const cleanupProvider = useCallback(() => {
    if (providerRef.current) {
      if (uriHandlerRef.current) {
        providerRef.current.off?.('display_uri', uriHandlerRef.current);
        uriHandlerRef.current = null;
      }
      providerRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    cancelledRef.current = true;
    setSelected(null);
    setView('select');
    setQrDataUrl('');
    setWcUri('');
    setWcError('');
    cleanupProvider();
  }, [cleanupProvider]);

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  useEffect(() => {
    return () => {
      cleanupProvider();
    };
  }, [cleanupProvider]);

  useEffect(() => {
    if (isConnected && open) {
      resetState();
      onClose();
    }
  }, [isConnected, open, resetState, onClose]);

  const handleBrowser = () => {
    setSelected('browser');
    connect(
      { connector: injected() },
      {
        onSuccess: () => { import('@/lib/analytics').then(a => a.trackEvent(a.Events.WALLET_CONNECTED, { method: 'browser' })); resetState(); onClose(); },
        onError: () => setSelected(null),
      }
    );
  };

  const handleMobile = async () => {
    if (!hasProjectId) return;
    cancelledRef.current = false;
    setSelected('mobile');
    setView('qr');
    setQrDataUrl('');
    setWcUri('');
    setWcError('');
    cleanupProvider();

    try {
      const provider = await EthereumProvider.init({
        projectId,
        chains: [137],
        showQrModal: false,
        optionalChains: [137],
        metadata: {
          name: 'Origin Sovereign',
          description: 'LGNS Yield Dashboard',
          url: window.location.origin,
          icons: [],
        },
      });

      if (cancelledRef.current) return;
      providerRef.current = provider;

      const onUri = async (uri: string) => {
        if (cancelledRef.current) return;
        setWcUri(uri);
        try {
          const dataUrl = await QRCode.toDataURL(uri, {
            width: 280,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' },
          });
          if (!cancelledRef.current) setQrDataUrl(dataUrl);
        } catch (e) {
          console.error('QR generation failed', e);
          if (!cancelledRef.current) setWcError('Failed to generate QR code');
        }
      };
      uriHandlerRef.current = onUri;
      provider.on('display_uri', onUri);

      await provider.connect();

      if (cancelledRef.current) return;

      if (provider.accounts && provider.accounts.length > 0) {
        const customConnector = createWcQrConnector(provider);
        connect(
          // @ts-expect-error wagmi connector type mismatch with WalletConnect provider
          { connector: customConnector },
          {
            onSuccess: () => { import('@/lib/analytics').then(a => a.trackEvent(a.Events.WALLET_CONNECTED, { method: 'mobile' })); resetState(); onClose(); },
            onError: (err) => {
              console.error('Wagmi connect via QR failed:', err);
              if (!cancelledRef.current) {
                setWcError('Connected on phone but failed to sync with app.');
              }
            },
          }
        );
      }
    } catch (e: any) {
      if (cancelledRef.current) return;
      console.error('WalletConnect error:', e);
      if (e?.message !== 'Connection request reset. Please try again.') {
        setWcError(e?.message || 'Connection failed');
      }
      setSelected(null);
      setView('select');
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    cancelledRef.current = true;
    cleanupProvider();
    setView('select');
    setSelected(null);
    setQrDataUrl('');
    setWcUri('');
    setWcError('');
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

          <motion.div
            className="relative w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/50 overflow-hidden"
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                {view === 'qr' && (
                  <button
                    onClick={handleBack}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-zinc-800 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <h3 className="text-lg font-bold text-white">
                  {view === 'select' ? 'Connect Wallet' : 'Scan QR Code'}
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-zinc-800 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {view === 'select' && (
              <>
                <p className="px-5 text-sm text-slate-400 mb-4">Choose how you want to connect</p>
                <div className="px-5 pb-5 space-y-3">
                  <button
                    onClick={handleBrowser}
                    disabled={isPending && selected === 'browser'}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-zinc-800 bg-zinc-900/50 hover:border-brand/40 hover:bg-brand/5 transition-all text-left group disabled:opacity-50"
                  >
                    <div className="h-11 w-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
                      <svg className="w-6 h-6 text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M21.8 10.2l-2-6A2 2 0 0017.9 3H6.1a2 2 0 00-1.9 1.2l-2 6a2 2 0 00-.1.8v2c0 .7.4 1.3 1 1.7V20a2 2 0 002 2h14a2 2 0 002-2v-5.3c.6-.4 1-1 1-1.7v-2c0-.3 0-.5-.1-.8zM6 20v-4h4v4H6zm8 0h-2v-5a1 1 0 00-1-1H5a1 1 0 00-1 1v5a.5.5 0 01-.5-.5V15h-.5v-4h18v4h-.5v.5a.5.5 0 01-.5.5V15h-5.5V20z"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white group-hover:text-brand transition-colors">
                        {isPending && selected === 'browser' ? 'Connecting...' : 'Browser Extension'}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">MetaMask, Rabby, Trust Wallet, etc.</p>
                    </div>
                    <svg className="w-4 h-4 text-slate-400 group-hover:text-brand transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <button
                    onClick={handleMobile}
                    disabled={selected === 'mobile' || !hasProjectId}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-zinc-800 bg-zinc-900/50 hover:border-brand/40 hover:bg-brand/5 transition-all text-left group disabled:opacity-50"
                  >
                    <div className="h-11 w-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                      <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white group-hover:text-brand transition-colors">
                        {selected === 'mobile' ? 'Connecting...' : 'Mobile Wallet'}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {hasProjectId ? 'Scan QR code with your phone wallet' : 'WalletConnect not configured'}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-slate-400 group-hover:text-brand transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </>
            )}

            {view === 'qr' && (
              <div className="px-5 pb-5">
                <p className="text-sm text-slate-400 mb-4 text-center">
                  Open your mobile wallet app and scan this QR code to connect
                </p>
                <div className="flex items-center justify-center">
                  <div className="w-[280px] h-[280px] rounded-xl overflow-hidden bg-white flex items-center justify-center">
                    {wcError ? (
                      <div className="flex flex-col items-center gap-2 px-4">
                        <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-red-500 text-xs text-center">{wcError}</p>
                      </div>
                    ) : qrDataUrl ? (
                      <img src={qrDataUrl} alt="WalletConnect QR Code" className="w-full h-full" />
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                        <p className="text-zinc-400 text-xs">Generating QR code...</p>
                      </div>
                    )}
                  </div>
                </div>
                {wcUri && (
                  <button
                    onClick={() => { navigator.clipboard.writeText(wcUri); }}
                    className="mt-3 w-full text-center text-xs text-slate-400 hover:text-brand transition-colors py-2"
                  >
                    Copy connection link
                  </button>
                )}
              </div>
            )}

            <div className="px-5 pb-4 pt-1 border-t border-zinc-800">
              <p className="text-xs text-slate-400 text-center">
                By connecting, you agree to the Terms of Service. No personal data is collected.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function WalletConnectModal(props: WalletConnectModalProps) {
  return createPortal(<WalletConnectModalContent {...props} />, document.body);
}

export function useWalletModal() {
  const [open, setOpen] = useState(false);
  return {
    isOpen: open,
    openModal: () => setOpen(true),
    closeModal: () => setOpen(false),
  };
}
